import { FC, useEffect, useState } from "react";

import { categoryNameLabelKeyForLocale, hasMultipleLocales } from "@simplyk/common";
import Stack from "@mui/material/Stack";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import {
  localeToCategoryFieldKey,
  RateCategory,
  useRateCategories,
} from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useRateCategories";
import { Add, Delete, Lists } from "@/icons/outlined";

interface NewCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (category: RateCategory) => void;
  defaultShowTranslation?: boolean;
}

interface NewCategoryFormData {
  primary: string;
  translation: string;
}

export const NewCategoryModal: FC<NewCategoryModalProps> = ({
  open,
  onClose,
  onCreated,
  defaultShowTranslation = false,
}) => {
  const { t } = useTranslate();
  const { createCategory, isCreating } = useRateCategories();
  const { apiFormObject, formHasTranslation, secondaryLocale } = useStableLiveFormEditorContext<"ticketing">();
  const primaryLocale = apiFormObject.locale;

  const [showTranslation, setShowTranslation] = useState(defaultShowTranslation);

  const form = useForm<NewCategoryFormData>({
    defaultValues: { primary: "", translation: "" },
    shouldUnregister: true,
  });
  const { control, formState, handleSubmit, reset } = form;

  useEffect(() => {
    if (!open) {
      reset({ primary: "", translation: "" });
      setShowTranslation(defaultShowTranslation);
    }
  }, [open, reset, defaultShowTranslation]);

  const onSubmit = handleSubmit(async (values) => {
    const payload: { en?: string; fr?: string; es?: string; de?: string } = {
      [localeToCategoryFieldKey(primaryLocale)]: values.primary,
    };
    const trimmedTranslation = values.translation?.trim();
    if (showTranslation && trimmedTranslation && secondaryLocale) {
      payload[localeToCategoryFieldKey(secondaryLocale)] = trimmedTranslation;
    }
    const { category, errorMessage } = await createCategory(payload);
    if (!category) {
      enqueueSnackbar(errorMessage || t("dashboard", "ticketing.categories.create.error"), { vibe: "danger" });
      return;
    }
    onCreated?.(category);
    onClose();
  });

  const primaryLabel =
    showTranslation && secondaryLocale
      ? t("dashboard", categoryNameLabelKeyForLocale(primaryLocale))
      : t("dashboard", "ticketing.categories.modal.name");

  const translationLabel = secondaryLocale ? t("dashboard", categoryNameLabelKeyForLocale(secondaryLocale)) : "";

  const organizationHasMultipleLocales = hasMultipleLocales(apiFormObject.organization?.country);

  return (
    <DashboardDialog open={open} onClose={onClose} maxWidth="sm">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<Lists />} title={t("dashboard", "ticketing.categories.modal.title")} />,
          content: (
            <DashboardDialogContent>
              <form noValidate onSubmit={onSubmit}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="flex-start">
                  <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }} spacing={0.5}>
                    <DashboardTextField
                      size="medium"
                      control={control}
                      name="primary"
                      label={primaryLabel}
                      placeholder={t("dashboard", "ticketing.categories.modal.placeholder")}
                      errorMessage={formState.errors.primary?.message}
                      rules={[isRequired(t("dashboard", "ticketing.categories.modal.required"))]}
                    />
                  </Stack>
                  {organizationHasMultipleLocales &&
                    !showTranslation &&
                    (formHasTranslation ? (
                      <DashboardButton
                        variant="text"
                        vibe="neutral"
                        startIcon={<Add />}
                        onClick={() => setShowTranslation(true)}
                        sx={{ alignSelf: "flex-end" }}
                      >
                        {t("dashboard", "ticketing.categories.modal.addTranslation")}
                      </DashboardButton>
                    ) : (
                      <DashboardTooltip
                        title={t("dashboard", "ticketing.categories.modal.addTranslationDisabledTooltip")}
                      >
                        <span style={{ alignSelf: "flex-end", display: "inline-flex" }}>
                          <DashboardButton variant="text" vibe="neutral" startIcon={<Add />} disabled>
                            {t("dashboard", "ticketing.categories.modal.addTranslation")}
                          </DashboardButton>
                        </span>
                      </DashboardTooltip>
                    ))}
                  {showTranslation && secondaryLocale && (
                    <>
                      <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }} spacing={0.5}>
                        <DashboardTextField
                          size="medium"
                          control={control}
                          name="translation"
                          label={translationLabel}
                          placeholder={t("dashboard", "ticketing.categories.modal.placeholder")}
                          errorMessage={formState.errors.translation?.message}
                        />
                      </Stack>
                      <DashboardIconButton
                        size="medium"
                        vibe="neutral"
                        aria-label={t("dashboard", "ticketing.categories.modal.removeTranslation")}
                        onClick={() => setShowTranslation(false)}
                        sx={{ alignSelf: "flex-end" }}
                      >
                        <Delete />
                      </DashboardIconButton>
                    </>
                  )}
                </Stack>
              </form>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton onClick={onSubmit} isLoading={isCreating}>
                {t("dashboard", "ticketing.categories.modal.save")}
              </DashboardButton>
              <DashboardButton vibe="neutral" variant="outlined" onClick={onClose}>
                {t("dashboard", "common.cancel")}
              </DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
